import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react';
import {
  BUNDLE_TAGS,
  CASE_TAGS,
  CASES_TAGS,
  MEDICAL_RECORDS_TAGS,
  MEDICAL_MATCH_TAGS,
  TIMELINE_CHRONOLOGY_TAGS,
  USERS_TAGS,
  GDPR_TAGS,
  TEMPLATE_TAGS,
} from '@/api/constants';
import { navigateMiddleware, storeRedirectPath } from '@/api';
import { AppRoutes } from '@/constants';
import type { RootState } from '@/store';
import { selectIsEndpointLockActive } from '@/store/slices/app/endpointLocks';
import { setServerLastCommunication } from '@/store/slices/app/serverLastCommunication';
import {
  announceEndpointLockRefusal,
  createEndpointLockError,
  ENDPOINT_LOCKS,
  findEndpointLock,
  getEndpointLockFromError,
  getEndpointPath,
} from './endpointLocks';

/**
 * Create our baseQuery instance
 */
const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_MERMAIDD_API_URL}/mermaidd/`,
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('mermaidd-api-key', `${import.meta.env.VITE_MERMAIDD_API_KEY}`);
    return headers;
  },
});

const baseQueryWithRetryAndRedirect: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = retry(
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    // case/status is polled to keep endpoint locks in step, so it says nothing about whether work was autosaved
    if (api && api.dispatch && !result.error && api.endpoint !== 'getCaseStatus') {
      //** Update last server communication with server */
      api.dispatch(setServerLastCommunication(Date.now()));
    }

    if (
      result.error?.status === 401 ||
      (result.error?.status === 'PARSING_ERROR' && result.error?.originalStatus === 401)
    ) {
      // Store the current path before redirecting to login
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        storeRedirectPath(currentPath);
      }
      navigateMiddleware(`/${AppRoutes.Login}`);
    }

    // An endpoint lock lasts far longer than the retries would, so retrying only delays the toast
    if (getEndpointLockFromError(result.error)) {
      retry.fail(result.error, result.meta);
    }

    return result;
  },
  {
    maxRetries: 3,
  },
);

/**
 * Refuses, without calling the server, any endpoint covered by an endpoint lock that is on (see `endpointLocks.ts`),
 * and announces those refusals and the backend's own so `useEndpointLockToasts` can explain them.
 */
const baseQueryWithEndpointLocks: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  baseQueryApi,
  extraOptions,
) => {
  const state = baseQueryApi.getState() as RootState;
  const lockId = findEndpointLock(getEndpointPath(args), (id) => selectIsEndpointLockActive(state, id));
  const result = lockId
    ? { error: createEndpointLockError(lockId) }
    : await baseQueryWithRetryAndRedirect(args, baseQueryApi, extraOptions);

  const refusedByLockId = getEndpointLockFromError(result.error);

  if (refusedByLockId) {
    // Announced rather than toasted here: importing the UI package into the api core breaks the app's startup order
    announceEndpointLockRefusal(refusedByLockId);
    baseQueryApi.dispatch(api.util.invalidateTags(ENDPOINT_LOCKS[refusedByLockId].refreshTags));
  }

  return result;
};

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  baseQuery: baseQueryWithEndpointLocks,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [
    ...BUNDLE_TAGS,
    ...CASE_TAGS,
    ...CASES_TAGS,
    ...MEDICAL_RECORDS_TAGS,
    ...MEDICAL_MATCH_TAGS,
    ...TIMELINE_CHRONOLOGY_TAGS,
    ...USERS_TAGS,
    ...GDPR_TAGS,
    ...TEMPLATE_TAGS,
  ],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
});
